import t from 'react-translate';
import React, { useContext, useEffect } from 'react';
import { css } from '@emotion/react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { config } from '@config/pendo.config.json';
import Audience from 'athena/components/audience';
import { useAppDispatch } from 'redux/store';
import {
  gray1,
  gray3,
  gray7,
  info,
  primary,
} from 'styles/global_defaults/colors';
import {
  doubleSpacing,
  halfSpacing,
  largeSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';
import { getCustomCommunicationPreview } from 'redux/actions/mentorship-program-custom-communications';
import { MentorshipProgramCustomCommunication } from 'redux/schemas/models/mentorship-program-custom-communications';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import {
  selectCustomCommunicationById,
  selectCustomCommunicationPreview,
} from 'redux/selectors/mentorship-program-custom-communications';
import MentoringProgramContext from '../../context';
import messageTypeMap, { DEFAULT_SESSION_NUDGE_WEEKS } from '../constants';
import { CustomCommunicationModalProps } from '.';
import { ModalMode } from '..';
import { commsModalCommonStyles } from './styles';
import InstitutionEmailHeader from './institution-email-header';

const styles = css`
  ${commsModalCommonStyles}
  gap: ${standardSpacing}px;

  .delivery-section {
    background: ${info};
    border-radius: ${halfSpacing}px;
    word-wrap: break-word;
    overflow-wrap: break-word;

    .audience {
      gap: ${halfSpacing}px;
    }
    .message-type {
      padding-bottom: 2px;
    }
    .sent-on {
      padding-bottom: 7px;
    }
  }
  .email-preview-header {
    padding-top: ${standardSpacing}px;
    border-top: 2px solid ${gray1};
  }
  .email-preview-content {
    padding-top: ${standardSpacing}px;
    border-top: 1px solid ${gray3};
    background: ${gray7};
    display: grid;
    grid-template-columns: 80px 1fr;
    gap: ${halfSpacing}px ${standardSpacing}px;

    > *:nth-of-type(2n) {
      min-width: 0;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    .email-preview-body {
      padding-right: 77px;
      .email-preview-body-message {
        padding: ${largeSpacing}px;

        a {
          color: ${primary};
          pointer-events: none;
          cursor: default;
        }
      }
    }
  }
  .view-communication-actions {
    gap: ${standardSpacing}px;
    padding-bottom: ${doubleSpacing}px;
  }
`;

const ViewCommunication = ({
  modalState,
  setModalState,
}: CustomCommunicationModalProps) => {
  const dispatch = useAppDispatch();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { id: programId, matchingType } = mentoringProgram || {};
  const mentoringProgramAliases = useSelector(
    (state) => getMentoringProgramAliases(state, programId),
  );
  const { name: institutionName, emailHeader, emailHeaderStyle } = useSelector(getCurrentInstitution) || {};
  const { communicationId } = modalState;
  const { subject, from, body } = useSelector(selectCustomCommunicationPreview) ?? {};
  const customCommunication: MentorshipProgramCustomCommunication = useSelector(
    selectCustomCommunicationById(communicationId),
  );
  const { type, target, enabled, sessionInactivityThresholdWeeks } = customCommunication || {};
  const communicationTranslationBase = t.MENTORING_PROGRAMS.COMMUNICATIONS.COMMUNICATION_MODAL;
  const translationBase = communicationTranslationBase.VIEW_MODE.EMAIL_PREVIEW;
  const dataQATag = config.pendo.athena.mentorshipProgram.communications.viewCommunicationModal;
  const { title: messageType, sentOn } = messageTypeMap(
    t,
    matchingType,
    mentoringProgramAliases,
    sessionInactivityThresholdWeeks ?? DEFAULT_SESSION_NUDGE_WEEKS,
  )[type];

  const previewHeader = [
    { title: translationBase.SUBJECT(), value: subject },
    { title: translationBase.FROM(), value: from },
  ];

  useEffect(() => {
    if (programId && communicationId) {
      dispatch(getCustomCommunicationPreview({ programId, communicationId }));
    }
  }, [dispatch, programId, communicationId]);

  return (
    <div css={styles} className='w-100 d-flex flex-column'>
      <div>
        {!enabled && (
          <div className='pb-1 text-right text-small color-gray-2 semi-bold'>
            {communicationTranslationBase.DISABLED()}
          </div>
        )}
        <div className='delivery-section p-4'>
          <div className='message-type black semi-bold text-regular'>
            {messageType}
          </div>
          <div className='sent-on color-gray-2 text-small'>{sentOn}</div>
          <Audience target={target} className='audience' />
        </div>
      </div>
      <div className='email-preview-header bold text-large-regular'>
        {translationBase.HEADER()}
      </div>
      <div className='email-preview-content'>
        {previewHeader.map(({ title, value }) => (
          <React.Fragment key={title}>
            <div className='text-right text-medium color-gray-2 align-content-center'>
              {title}
            </div>
            <div className='black text-body'>{value}</div>
          </React.Fragment>
        ))}
        <div />
        <div className='email-preview-body pt-1'>
          <InstitutionEmailHeader
            emailHeader={emailHeader}
            emailHeaderStyle={emailHeaderStyle}
          />
          <div
            className='email-preview-body-message bg-white text-body black'
            dangerouslySetInnerHTML={{
              __html: body,
            }}
          />
          <div className='text-center pt-3 pb-4'>
            {translationBase.COPYRIGHT(institutionName, `${new Date().getFullYear()}`)}
          </div>
        </div>
      </div>
      <div className='view-communication-actions d-flex justify-content-center'>
        <Button
          size='lg'
          variant='secondary'
          onClick={() => setModalState({ mode: null, communicationId: null })}
          data-qa={dataQATag.closeButton}
        >
          {t.FORM.CLOSE()}
        </Button>
        <Button
          size='lg'
          variant='primary'
          onClick={() => setModalState({ mode: ModalMode.EDIT, communicationId, previousMode: ModalMode.VIEW })}
          data-qa={dataQATag.editButton}
        >
          {t.FORM.EDIT()}
        </Button>
      </div>
    </div>
  );
};

export default ViewCommunication;
